/* eslint-disable no-param-reassign */
import React, { CSSProperties } from 'react';
import ReactDOM, { findDOMNode } from 'react-dom';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import Loading from '@tencent/dui/lib/components/Loading';

import Style from './index.less';
import { browser } from '../../../../slide/infrastructure/module/independent/agent/common';
import { TDWOperationType, TDWModuleType, TDWActionType } from '../../../../slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import i18n from '@i18next/i18n';
import { transform2rgba, rgbaToHexA, rgbaToRgb } from './utils';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';

const INIT_NUMBER = 10;
const GLASS_SIZE = 194;
const SCALE = 1;

const CURRENT_SLIDE_CLASSNAME = 'currentSlide';
const DOM_ATTRIBUTE_ID = 'id';
const SLIDE_CONTAINER_ID = 'slide';

let loading: ReturnType<typeof Snackbar.show>|null = null;
let loadingTimer: NodeJS.Timeout|null = null;

interface ColorPickerProps {
    imageConfig: { width: number; height: number; };
    point: object;
    onVisibleChange: (visible) => void;
    autoTest: boolean;
    testPoint: { pointX: number; pointY: number; };
    handleColorChange: (color: TDColor) => void;
    imageUrl: string;
}

interface ColorPickerState {
    isShown: boolean;
    glassLeft: number;
    glassTop: number;
    visibility: CSSProperties['visibility'];
    color: CSSProperties['color'];
    isReady?: boolean;
}

export default class ColorPicker extends React.Component<ColorPickerProps, ColorPickerState> {
    public static ColorPickerOp;

    public static getCurrentSlideNode(node) {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let index = 0; index < node.childNodes.length; index++) {
            const child = node.childNodes[index];
            if (child.classList.contains(CURRENT_SLIDE_CLASSNAME)) {
                return child;
            }
            if (node.getAttribute(DOM_ATTRIBUTE_ID) !== SLIDE_CONTAINER_ID) {
                return ColorPicker.getCurrentSlideNode(child);
            }
        }
        return null;
    }

    public static cloneShadowDom(node, cloneNode) {
        const currentSlideNode = ColorPicker.getCurrentSlideNode(node);
        const currentSlideCloneNode = ColorPicker.getCurrentSlideNode(cloneNode);
        if (!currentSlideNode || !currentSlideCloneNode) return;

        for (let index = 0; index < currentSlideNode.childNodes.length; index++) {
            const childNode = currentSlideNode.childNodes[index];
            if (childNode.shadowRoot) {
                const fragment = document.createDocumentFragment();
                childNode.shadowRoot.childNodes.forEach((elemntNode) => {
                    fragment.appendChild(elemntNode.cloneNode(true));
                });

                const cloneChildNode = currentSlideCloneNode.childNodes[index];
                cloneChildNode.appendChild(fragment);
            }
        }
    }

    public static create(dom, config) {
        return new Promise((resolve, reject) => {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC,
                action: TDWActionType.COLOR_PICKER,
            });

            document.body.addEventListener('mousemove', handleMouseMove);
            if (browser.isSafari) {
                const version = navigator.userAgent.match(/Version\/(\d+)(?:\.(\d+))?(?:\.(\d+))?/);
                if (!version) return;
                if ((parseInt(version[1], 10) < 12)
                    || ((parseInt(version[1], 10) === 12) && (parseInt(version[2], 10) < 1))) {
                    Snackbar.show({
                        message: i18n.t('当前浏览器版本过低，请升级至 12.1 以上版本或在 Chrome 中使用该功能'),
                        duration: 5000,
                    });
                    return;
                }
            }

            if (browser.isEdge) {
                Snackbar.show({
                    message: i18n.t('取色器暂不支持本浏览器，请在chrome中使用'),
                    duration: 5000,
                });
                return;
            }

            loadingTimer = setTimeout(() => {
                loading = Loading.show({});
            }, 500);

            let scale = dom.style.transform;
            scale = scale ? parseFloat(scale.substring(scale.indexOf('(') + 1, scale.indexOf(')'))) : 1;
            const width = parseFloat(dom.style.width);
            const height = parseFloat(dom.style.height);
            const imageConfig = {
                width: width * scale,
                height: height * scale,
            };

            function filter(node) {
                const notIframe = node.tagName !== 'IFRAME';
                let isVisible = true;
                if (node instanceof HTMLElement && window.getComputedStyle(node)?.display === 'none') {
                    isVisible = false;
                }
                return notIframe && isVisible;
            }

            const options = {
                useCORS: true,
                ...imageConfig,
                filter,
                cloneNode: true,
            };
            const copyPasteImageService = (AppServiceProvider.getInstance()
                .getAppServices()as IPcEditorAppServices).editableServiceLayer?.copyPasteImageService;
            copyPasteImageService?.promise().then((module) => {
                const { domtoimage } = module || { };
                domtoimage?.toSvg(dom, options).then((dataUrl) => {
                    config = {
                        imageConfig,
                        imageUrl: dataUrl,
                        ...config,
                    };
                    const div = document.createElement('div');
                    const domParent = dom.parentNode;
                    domParent.appendChild(div);
                    // eslint-disable-next-line react/no-render-return-value
                    const instance = ReactDOM.render(<ColorPicker {...config} />, div);
                    resolve(instance);

                    document.body.removeEventListener('mousemove', handleMouseMove);
                })
                    .catch((err) => {
                        reject(err);
                    });
            });
            function handleMouseMove(e) {
                const point = {
                    clientX: e.clientX,
                    clientY: e.clientY,
                };
                if (config) {
                    config.point = point;
                }
            }
        });
    }

    public static show(dom, config) {
        if (!this.ColorPickerOp) {
            return this.create(dom, config).then((picker) => {
                this.ColorPickerOp = picker;
                this.ColorPickerOp.show();
            })
                .catch((e) => {
                    window.log.error(`取色器生成截图失败, ${e}`);
                    if (loadingTimer) {
                        clearTimeout(loadingTimer);
                    }
                    if (loading) {
                        loading.close();
                    }
                });
        }
        this.ColorPickerOp.show();
    }

    public static hide() {
        if (this.ColorPickerOp) {
            this.ColorPickerOp.hide();
            this.ColorPickerOp = null;
        }
    }

    public static isShown() {
        if (this.ColorPickerOp) {
            return this.ColorPickerOp.isShown();
        }
        return false;
    }

    private imageCanvasRef: HTMLCanvasElement|null;
    private imageCtx:  CanvasRenderingContext2D|null|undefined;
    private glassRef: HTMLCanvasElement|null;
    private glassCtx:  CanvasRenderingContext2D|null|undefined;
    private imageRef: HTMLImageElement|null;
    private point: {
        pointX: number;
        pointY: number;
    };
    private containerRef: HTMLDivElement|null;

    public constructor(props, context) {
        super(props, context);
        this.state = {
            isShown: false,
            glassLeft: 0,
            glassTop: 0,
            visibility: 'hidden',
            color: '#fff',
            isReady: false,
        };
    }

    public componentDidMount() {
        if (loadingTimer) {
            clearTimeout(loadingTimer);
        }
        if (loading) {
            loading.close();
        }

        const { width, height } = this.props.imageConfig;
        if (this.imageCanvasRef) {
            this.imageCanvasRef.width = width;
            this.imageCanvasRef.height = height;
        }
        this.imageCtx = this.imageCanvasRef?.getContext('2d');
        this.glassCtx = this.glassRef?.getContext('2d');
        if (this.imageRef) {
            this.imageRef.crossOrigin = '';
            this.imageRef.onload = () => {
                setTimeout(() => {
                    this.init();
                }, 50);
            };
        }
    }

    public init() {
        this.renderImageCanvas();
        if (!this.point && this.props.point) {
            this.getPointPosition(this.props.point);
            this.drawGlass();
            this.getColor();
        } else if (this.point) {
            this.drawGlass();
            this.getColor();
        }
    }

    public componentWillUnmount() {
        this.removeListener();
    }

    public render() {
        const {
            isShown,
            glassLeft,
            glassTop,
            visibility,
            color,
        } = this.state;
        const {
            imageConfig,
            imageUrl,
        } = this.props;

        return (
            <div
                className={Style['color-picker-wrapper']}
                style={{
                    display: isShown ? 'block' : 'none',
                    width: imageConfig.width,
                    height: imageConfig.height,
                }}
                ref={(el) => {
                    this.containerRef = el;
                }}
                onContextMenu={this.handleContextMenu}
            >
                <img
                    className={Style['color-picker-image']}
                    src={imageUrl}
                    ref={(el) => {
                        this.imageRef = el;
                    }} />
                <canvas
                    className={'color-picker-canvas'}
                    ref={(el) => {
                        this.imageCanvasRef = el;
                    }}
                    style={{ opacity: 0 }}
                    onClick={this.handleClick}
                    onMouseMove={this.handleMove}
                    onMouseLeave={this.handleLeave}
                    onMouseDown={this.preventBlur}
                />
                <div className={Style['color-picker-glass-wrapper']}
                    style={{ left: glassLeft, top: glassTop, visibility }}>
                    <div className={Style['color-picker-glass']}>
                        <canvas
                            ref={(el) => {
                                this.glassRef = el;
                            }}
                            width={GLASS_SIZE}
                            height={GLASS_SIZE}
                            style={{ width: GLASS_SIZE, height: GLASS_SIZE }}
                        />
                        <div className={Style['color-picker-rect']}></div>
                        <div className={Style['color-picker-color']}>
                            {`${rgbaToRgb(color)} ${rgbaToHexA(color).hex}`}
                        </div>
                        <div className={Style['color-picker-canvas-wrapper']}></div>
                    </div>
                </div>
            </div>
        );
    }

    private removeListener = () => {
        document.body.removeEventListener('click', this.handleBodyClick);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('keydown', this.handleKeyDown);
    };

    private handleBodyClick = (e) => {
        setTimeout(() => {
            if (this.state.isShown && !this.isClickOnContent(e)) {
                ColorPicker.hide();
            }
        }, 50);
    };

    private isClickOnContent = (e) => {
        const { clientX, clientY } = e.target;
        if (this.imageRef) {
            const rect = this.imageRef.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right
                && clientY >= rect.top && clientY <= rect.bottom) {
                return true;
            }
        }
        return false;
    };

    private handleResize = (e) => {
        // 用户操作触发 resize 事件时，隐藏取色器
        if (e.isTrusted) {
            ColorPicker.hide();
        }
    };

    private handleKeyDown = (e) => {
        if (!this.isShown()) return;

        if (e.keyCode === 27) {
            ColorPicker.hide();
        } else if (e.keyCode === 13 || e.keyCode === 32) {
            this.handleClick();
        }
    };

    private preventBlur = (e) => {
        e.preventDefault();
    };

    private show = () => {
        if (!this.state.isShown) {
            document.body.addEventListener('click', this.handleBodyClick);
            window.addEventListener('resize', this.handleResize);
            window.addEventListener('keydown', this.handleKeyDown);
            this.onVisibleChange(true);
            this.setState({
                isShown: true,
            });
        }
    };

    private hide = () => {
        if (this.state.isShown) {
            this.setState({
                isShown: false,
            });
            this.onVisibleChange(false);
            // eslint-disable-next-line react/no-find-dom-node
            const dom = findDOMNode(this.containerRef);
            const domParent = dom?.parentNode?.parentNode;
            if (dom && domParent) {
                domParent.removeChild(dom.parentNode);
            }
        }
    };

    private onVisibleChange = (visible) => {
        if (this.props.onVisibleChange) {
            this.props.onVisibleChange(visible);
        }
    };

    private isShown = () => this.state.isShown;

    private renderImageCanvas = () => {
        const { width, height } = this.props.imageConfig;
        if (this.imageCtx) {
            this.imageCtx.fillStyle = '#f2f2f2';
            this.imageCtx.fillRect(0, 0, width, height);
            if (this.imageRef) {
                this.imageCtx.drawImage(this.imageRef, 0, 0, width, height);
            }
        }
        this.setState({
            isReady: true,
        }, () => {
            setTimeout(() => {
                // 自动化测试时执行
                if (this.props.autoTest) {
                    this.point = {
                        pointX: this.props.testPoint.pointX,
                        pointY: this.props.testPoint.pointY,
                    };
                    this.handleClick();
                }
            }, 50);
        });
    };

    private getPointPosition = (e) => {
        const { left, top } = this.imageCanvasRef?.getBoundingClientRect() || {};
        this.point = {
            pointX: Math.floor(e.clientX - (left || 0)),
            pointY: Math.floor(e.clientY - (top || 0)),
        };
    };

    private getColor = () => {
        const { pointX, pointY } = this.point;
        const { data } = this.imageCtx?.getImageData(pointX, pointY, 1, 1) || {};
        const color = transform2rgba(data);
        this.setState({
            color,
        });
    };

    private handleContextMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    private handleClick = () => {
        this.getColor();
        this.props.handleColorChange?.(new TDColor({
            rgbaStr: this.state.color,
        }));
        ColorPicker.hide();
    };

    private handleMove = (e) => {
        this.getPointPosition(e);
        this.drawGlass();
        this.getColor();
    };

    private handleLeave = () => {
        this.clearGlassRect();
    };

    private drawGlass = () => {
        if (!this.point || !this.state.isReady) return;
        if (this.imageRef?.complete && this.state.visibility === 'hidden') {
            this.setState({
                visibility: 'visible',
            });
        }

        const { pointX, pointY } = this.point;
        const glassLeft = Math.floor(pointX - (GLASS_SIZE / 2)) - 4;
        const glassTop = Math.floor(pointY - (GLASS_SIZE / 2)) - 4;
        this.setState({
            glassTop,
            glassLeft,
        });

        this.drawImageSmoothingEnable(this.glassCtx, false);

        if (this.imageCanvasRef) {
            this.glassCtx?.drawImage(
                this.imageCanvasRef,
                Math.floor(pointX - ((GLASS_SIZE / 2) / (SCALE * INIT_NUMBER))),
                Math.floor(pointY - ((GLASS_SIZE / 2) / (SCALE * INIT_NUMBER))),
                Math.floor((GLASS_SIZE + INIT_NUMBER) / (SCALE * INIT_NUMBER)),
                Math.floor((GLASS_SIZE + INIT_NUMBER) / (SCALE * INIT_NUMBER)),
                -INIT_NUMBER, -INIT_NUMBER,
                GLASS_SIZE + INIT_NUMBER, GLASS_SIZE + INIT_NUMBER,
            );
        }
    };

    private clearGlassRect = () => {
        this.glassCtx?.clearRect(0, 0, GLASS_SIZE, GLASS_SIZE);
        this.setState({
            visibility: 'hidden',
        });
    };

    private drawImageSmoothingEnable = (context, enable) => {
        context.mozImageSmoothingEnabled = enable;
        context.webkitImageSmoothingEnabled = enable;
        context.msImageSmoothingEnabled = enable;
        context.imageSmoothingEnabled = enable;
    };
}
